/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService, TimeService, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';

import { type SmartguiDataTurboshareReceived } from '../share';


export const SYSTEM_VSTORE_NAME = 'System_vStore';
export const SYSTEM_VSTORE_ID = '0';

/**
 * 域类型
 */
export enum DomainTypeE {
    AD = 0,
    LDAP = 1,
    Local = 2,
    NIS = 3
}

/**
 * 权限
 */
export enum PermissionTypeE {
    Read = 0,
    ReadAndWrite = 1,
    None = 5
}

/**
 * 用户类型
 * 0 Everyone
 * 1 本地用户
 * 2 本地用户组
 * 3 AD域用户
 * 4 AD域用户组
 * 5 DataTurbo用户
 */
export enum UserTypeE {
    Everyone = 0,
    User = 1,
    UserGroup = 2,
    AD = 3,
    ADGroup = 4,
    DataTurbo = 5
}

/**
 * 名称类型
 */
export enum AuthNameE {
    /**
     * Everyone
     */
    Everyone = 0,
    /**
     * 用户
     */
    Single = 1,
    /**
     * 用户组
     */
    Group = 2
}

/**
 * 创建类型
 * newCreate 新创建
 * copyfrom 模板创建
 */
export enum createTypeE {
    new = 'newCreate',
    copyFrom = 'copyFrom'
}

export enum shareNameE {
    maxLen = 80
}

export interface ErrorMessage {
    name?: { required: string; rangeByteLength: string; dataTurboShareName: string };
    DataTurbocopyFromSelect?: { required: string };
    filesystem?: { required: string };
    description?: { rangeByteLength: string };
}
@Injectable()
export class DataTurboShareStrService {
    i18n = {
        btn: {
            createAsBtn: this.get('common.createSchema_btn'),
            createBtn: this.get('common.create_button'),
            deleteBtn: this.get('common.delete_button'),
            modifyBtn: this.get('common.modify_button'),
            okBtn: this.get('common.confirm_button'),
            cancelBtn: this.get('common.cancel_button'),
            closeBtn: this.get('common.close_button'),
            actionBtn: this.get('common.operation_button'),
            addBtn: this.get('common.add_button'),
            removeBtn: this.get('common.remove_button'),
            addClient: this.get('service.share_term_addClient_button'),
            addUserBtn: this.get('service.quota_term_create_addUser_title'),
        },
        label: {
            name: this.get('common.name_column'),
            description: this.get('common.description_column'),
            fileSystem: this.get('service.quota_term_create_FS_lab'),
            vStoreLab: this.get('common.owningVstore_lab'),
            dtree: this.get('service.quota_term_create_Dtree_lab'),
            share_path: this.get('service.share_term_sharePath_lab'),
            share_name: this.get('service.share_term_shareName_lab'),
            charEncoding: this.get('service.share_term_charEncoding_lab'),
            id: this.get('common.ID_column'),
            type: this.get('common.type_column'),
            permissionLevel: this.get('service.share_term_addClient_permissionLevel_lab'),
            advance: this.get('common.advance_label'),
            comma: this.get('common.comma_label'),
            createStyle: this.get('service.share_term_createFrom_lab'),
            copyFrom: this.get('service.share_term_createMode_copyfrom_value'),
            new: this.get('common.newCreate_checkbox'),
            share: this.get('common.EnumDefine_MOTYPE_SHARE'),
            DataTurboUser: this.get('service.share_term_createDataTurbo_userType_dataTurbo_value'),
            leftTransferTitle: this.get('service.share_term_createDataTurbo_availUser_lab'),
            rightTransferTitle: this.get('service.share_term_createDataTurbo_selectedUser_lab'),
            everyone: this.get('service.share_term_createDataTurbo_addUser_userType_everyone_listbox'),
            dataTurbo: this.get('service.share_term_createDataTurbo_userType_dataTurbo_value'),
            readAndWritePermission: this.get('protection.hyperMetroCG_view_localResourceHostStatus_readAndWrite_value'),
        },
        title: {
            dataTurbo: this.get('common.DataTurbo_lab'),
            // 模板创建DataTurbo
            createAsTitle: this.get('service.share_term_createDataTurbofromTemp_title'),
            // 创建DataTurbo
            createTitle: this.get('service.share_term_createDataTurbo_title'),
            basicInfo: this.get('common.basicInfo_title'),
            selectFS: this.get('service.share_term_create_chooseFilesystem_title'),
            selectDtree: this.get('service.share_term_create_chooseDtree_title'),
            deleteShare: this.get('service.share_term_deleteDataTurbo_title'),
            modifyShare: this.get('service.share_term_modifyDataTurbo_title'),
            permissionList: this.get('service.share_term_view_permissions_lab'),
            addDataTurboPermission: (name: string, permission: string): string =>
                this.get('rest.addUserPermission_msg', [name, permission]),
            addDataTurboUser: this.get('service.quota_term_create_addUser_title'),
        },
        tip: {
            // 请先选择所属租户。
            plsSelectVstore: this.get('service.share_term_create_chooseVstore_tip'),
            // 请先选择copyfrom的共享。
            plsSlectShare: this.get('service.share_term_create_modify_err_tip'),
            // 权限数{0}。
            permisssions: (num: string): string => this.get('service.share_term_create_permissionNum_lab', [num]),
            // 权限数{0},{1}
            permissionsWithAllClient: (num: number, str: string): string =>
                this.get('service.share_term_create_permissionNum_value_lab', [num, str]),
            // 权限数{0},{1}
            permissionsWithEveryone: (num: number, str: string): string =>
                this.get('service.share_term_create_permissionNum_value_lab', [num, str]),
            // 只有加入权限列表的客户端才能访问
            clientsTip: this.get('service.share_term_createDataTurbo_addUser_desc_tip'),
            permissionClearTip: this.get('service.share_term_clearPermissions_msg'),
            shareNameValidation: this.get('service.share_term_createCIFS_shareName_validation'),
            everyone: this.get('service.share_term_create_permission_everyoneControl_value'),
            everyoneRead: this.get('service.share_term_create_permission_everyoneRead_value'),
            // 该Dtree路径中含有“\”，在CIFS共享中“\”为目录分隔符，会导致创建的CIFS共享路径与预期不符。
            dtreePathHasBackslash: this.get('service.share_term_createCIFS_selectDtree_validation'),
        },
        error: {
            copyFromRequired: this.get('service.share_term_createMode_copyfrom_valid'),
            hostName: this.get('service.share_term_addClient_nameorIPaddress_valid', [1, 255]),
            netName: this.get('service.share_term_addClient_type_networkGroup_name_validation'),
            atLeastSelectOneTip: this.get('common.selectOneObject_lab'),
            adUserValidation: this.get('service.share_term_createCIFS_addADUser_name_validation1'),
            adGroupValidation: this.get('service.share_term_createCIFS_addADUserGroup_name_validation'),
            alreadySelected: this.get('service.share_term_createCIFS_addUser_exist_tip'),
            // 不能包含*:\\?”<>|中的任意一个特殊字符，且每个特殊字符串(%d或%w)只能出现一次。
            relative_path: this.get('service.share_term_createCIFSHomedir_relativeDirectory_valid'),
            // 用户名不能包含字符\"[]<>+:;,?=|@和空格，“\\”和“.”不能作为首尾字符，“*”只能位于末尾，“\\*”只能出现一次。
            userName: this.get('service.share_term_createCIFSHomedir_userName_err_valid'),
        },
        highRisk: {
            deleteDataTurbo: this.get('service.share_deleteDataTurbo_task_removeShare_confirmation_message'),
        },
        rest: {
            // 创建DataTurbo共享{0}
            createDataTurbo: this.get('rest.CreateDATATURBOShare_msg'),
            // 修改DataTurbo共享{0}
            modifyDataTurbo: this.get('rest.ModifyDATATURBOShare_msg'),
            // 删除DataTurbo共享{0}
            deleteDataTurbo: this.get('rest.DeleteDATATURBOShare_msg'),
            // 增加DataTurbo共享用户{0}
            addDataTurboClient: this.get('rest.addDATATURBOUserorGroup_msg'),
            // 移除DataTurbo共享用户{0}
            deleteDataTurboClient: this.get('rest.removeDATATURBOUserorGroup_msg'),
        },
    };

    /**
     * windows用户、用户组类型
     */
    userTypeOpts: SelectOption[] = [
        // 暂时只支持dataturbo类型
        {
            label: this.i18n.label.dataTurbo,
            value: UserTypeE.DataTurbo,
        },
    ];

    /**
     * 权限级别
     * 只读 0
     * 读写 1
     */
    permissionLevelOpts: SelectOption[] = [
        {
            label: this.i18nService.get('settings.userAndRole_term_readonly_list'),
            value: PermissionTypeE.Read,
        },
        {
            label: this.i18nService.get('protection.hyperMetroCG_view_localResourceHostStatus_readAndWrite_value'),
            value: PermissionTypeE.ReadAndWrite,
        },
        {
            label: this.i18nService.get('common.term_confirm_none'),
            value: PermissionTypeE.None,
        },
    ];

    /**
     * 权限级别(升级日志文件系统创DataTurbo共享客户端只允许只读权限)
     * 只读 0
     */
    auditLogFsPermissionLevelOpts: SelectOption[] = [
        {
            label: this.i18nService.get('settings.userAndRole_term_readonly_list'),
            value: PermissionTypeE.Read,
        },
    ];

    /**
     * 613与二级存储原有的字符编码
     */
    charEncodingTypeObj = {
        0: this.i18nService.get('service.share_term_charEncoding_0'),
        11: this.i18nService.get('service.share_term_charEncoding_11'),
        12: this.i18nService.get('service.share_term_charEncoding_12'),
        13: this.i18nService.get('service.share_term_charEncoding_13'),
        14: this.i18nService.get('service.share_term_charEncoding_14'),
        21: this.i18nService.get('service.share_term_charEncoding_21'),
        22: this.i18nService.get('service.share_term_charEncoding_22'),
        23: this.i18nService.get('service.share_term_charEncoding_23'),
        30: this.i18nService.get('service.share_term_charEncoding_30'),
        31: this.i18nService.get('service.share_term_charEncoding_31'),
        32: this.i18nService.get('service.share_term_charEncoding_32'),
        33: this.i18nService.get('service.share_term_charEncoding_33'),
        34: this.i18nService.get('service.share_term_charEncoding_34'),
        40: this.i18nService.get('service.share_term_charEncoding_40'),
    };

    charEncodingType = this.charEncodingTypeObj;

    constructor(
        private i18nService: I18nService,
        private timeService: TimeService,
        private utilService: UtilService,
    ) {
    }

    /**
     * 获取用户类型
     */
    getUserType = (data: SmartguiDataTurboshareReceived): UserTypeE => {
        const authName: AuthNameE = Number(data.NAME);
        switch (authName) {
            case AuthNameE.Everyone: return UserTypeE.Everyone;
            case AuthNameE.Single: return UserTypeE.DataTurbo;
            default:
                return UserTypeE.Everyone;
        }
    };

    get(key: string, params?: any[] | boolean, colon?: boolean): string {
        return this.i18nService.get(key, params, colon);
    }

    requiredErr(name: string): string {
        return this.get('common.null_validation', [name]);
    }

    rangeByteLengthErr(min: number, max: number): string {
        return this.get('common.length_msg', [min, max]);
    }

    rangeByteOneLengthErr(min: number, max: number): string {
        return this.get('common.length_oneValue_tip', [min, max]);
    }

    commonNameErr(name: string): string {
        return this.get('common.name_vadlitaion', [name]);
    }

    deleteResult(name: string): string {
        return this.get('rest.delete_result_tip', [name]);
    }

    repeatErr(name: string): string {
        return this.get('common.existence_validation', [name]);
    }

    getPath(path: string): string {
        return '/'.concat(path.concat('/'));
    }

    /**
     * 判断是否有\ \要转义才能判断
     */
    isPathHasBackslash(path: string): boolean {
        return path.indexOf('\\') !== -1;
    }

    getTimeStamp(): string {
        return this.timeService.formatUTC(this.timeService.currTime * 1e3, 'YYYYMMDDHHmmss', false).split(' ')[0];
    }

    getShareName(fsName: string, timestamp: string): string {
        const sliceStr = '_';
        const timeLength = timestamp.length;
        return this.utilService.byteSubstring(fsName, shareNameE.maxLen - timeLength - 1) + sliceStr + timestamp;
    }
}
